import { supabase } from '../lib/supabase';

// ─── Helpers ────────────────────────────────────────────────────────────────

const toConversation = (conv) => ({
  id: conv?.id,
  studentId: conv?.student_id,
  instituteId: conv?.institute_id,
  status: conv?.status,
  handlerType: conv?.handler_type,
  handedOffAt: conv?.handed_off_at,
  handoffReason: conv?.handoff_reason,
  aiMessageCount: conv?.ai_message_count || 0,
  humanMessageCount: conv?.human_message_count || 0,
  escalationCount: conv?.escalation_count || 0,
  lastMessageAt: conv?.last_message_at,
  createdAt: conv?.created_at,
  student: conv?.student,
  institute: conv?.institute,
});

const toMessage = (msg, senderData) => ({
  id: msg?.id,
  conversationId: msg?.conversation_id,
  senderId: msg?.sender_id,
  message: msg?.message,
  fileUrl: msg?.file_url,
  messageType: msg?.message_type || 'text',
  senderType: msg?.sender_type || 'human',
  aiModel: msg?.ai_model,
  aiConfidence: msg?.ai_confidence,
  escalationTrigger: msg?.escalation_trigger,
  sentAt: msg?.sent_at,
  sender: senderData || msg?.sender || null,
});

// ─── Service ─────────────────────────────────────────────────────────────────

export const chatService = {
  // ── Conversations ──────────────────────────────────────────────────────────

  async getConversations(userId, userRole) {
    const filterField = userRole === 'student' ? 'student_id' : 'institute_id';

    const { data, error } = await supabase?.from('chat_conversations')?.select(`
        *,
        student:student_id(id, name, email),
        institute:institute_id(id, name, email)
      `)?.eq(filterField, userId)?.order('last_message_at', { ascending: false });

    if (error) throw error;
    return (data || [])?.map(toConversation);
  },

  async createConversation(studentId, instituteId) {
    // Return existing conversation if one already exists
    const { data: existing } = await supabase?.from('chat_conversations')?.select('*')?.eq('student_id', studentId)?.eq('institute_id', instituteId)?.maybeSingle();

    if (existing) return existing;

    const { data, error } = await supabase?.from('chat_conversations')?.insert({ student_id: studentId, institute_id: instituteId, status: 'active' })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // ── Messages ───────────────────────────────────────────────────────────────

  async getMessages(conversationId) {
    const { data, error } = await supabase?.from('chat_messages')?.select(`
        *,
        sender:sender_id(id, name, email)
      `)?.eq('conversation_id', conversationId)?.order('sent_at', { ascending: true });

    if (error) throw error;
    return (data || [])?.map((msg) => toMessage(msg));
  },

  async sendMessage(
    conversationId,
    senderId,
    message,
    messageType = 'text',
    fileUrl = null,
    senderType = 'human',
    aiModel = null,
    aiConfidence = null,
    escalationTrigger = null
  ) {
    const { data, error } = await supabase?.from('chat_messages')?.insert({
        conversation_id: conversationId,
        sender_id: senderId,
        message,
        message_type: messageType,
        file_url: fileUrl,
        sender_type: senderType,
        ai_model: aiModel,
        ai_confidence: aiConfidence,
        escalation_trigger: escalationTrigger,
      })?.select()?.single();

    if (error) throw error;

    // Update conversation last_message_at (fire-and-forget)
    supabase?.from('chat_conversations')?.update({ last_message_at: new Date()?.toISOString() })?.eq('id', conversationId)?.then(() => {});

    // Notify recipient (fire-and-forget, only for human messages)
    if (senderType === 'human') {
      this._notifyRecipient(conversationId, senderId, message)?.catch(() => {});
    }

    return toMessage(data);
  },

  async _notifyRecipient(conversationId, senderId, message) {
    try {
      const { notificationService } = await import('./notificationService');
      const { data: conversation } = await supabase?.from('chat_conversations')?.select('student_id, institute_id')?.eq('id', conversationId)?.single();

      const recipientId =
        senderId === conversation?.student_id
          ? conversation?.institute_id
          : conversation?.student_id;

      const { data: sender } = await supabase?.from('users')?.select('name')?.eq('id', senderId)?.single();

      await notificationService?.notifyChatMessage({
        userId: recipientId,
        conversationId,
        senderName: sender?.name || 'Someone',
        messagePreview: message?.substring(0, 100),
      });
    } catch (_) {
      // Notification failure must never break chat
    }
  },

  // ── Real-time Subscriptions ────────────────────────────────────────────────

  /**
   * Subscribe to new messages in a specific conversation.
   * Returns an unsubscribe function.
   *
   * Strategy: Use a unique channel name per conversation.
   * On INSERT, fetch the full message row (with sender) and call callback.
   */
  subscribeToMessages(conversationId, callback) {
    const channelName = `chat_messages_conv_${conversationId}`;

    const channel = supabase?.channel(channelName, { config: { broadcast: { self: false } } })?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        async (payload) => {
          const raw = payload?.new;
          if (!raw) return;

          // Fetch sender name so the message renders correctly
          let senderData = null;
          if (raw?.sender_id) {
            const { data } = await supabase?.from('users')?.select('id, name, email')?.eq('id', raw?.sender_id)?.single();
            senderData = data;
          }

          callback(toMessage(raw, senderData));
        }
      )?.subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log(`[Chat] ✅ Realtime subscribed: ${channelName}`);
        } else if (status === 'CHANNEL_ERROR') {
          console.error(`[Chat] ❌ Channel error: ${channelName}`, err);
        } else if (status === 'TIMED_OUT') {
          console.warn(`[Chat] ⏱ Timed out: ${channelName}`);
        }
      });

    return () => {
      supabase?.removeChannel(channel);
    };
  },

  /**
   * Subscribe to conversation-level changes for a user.
   * Fires callback whenever a conversation is inserted/updated
   * OR a new message arrives (so the sidebar can refresh).
   * Returns an unsubscribe function.
   */
  subscribeToConversations(userId, userRole, callback) {
    const filterField = userRole === 'institute' ? 'institute_id' : 'student_id';
    const channelName = `chat_conversations_${userRole}_${userId}`;

    const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_conversations',
          filter: `${filterField}=eq.${userId}`,
        },
        (payload) => {
          callback({ type: 'CONVERSATION_CHANGE', payload });
        }
      )?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
        },
        (payload) => {
          callback({ type: 'MESSAGE_INSERT', payload });
        }
      )?.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`[Chat] ✅ Conversations subscribed: ${channelName}`);
        }
      });

    return () => {
      supabase?.removeChannel(channel);
    };
  },
};